import {useState} from 'react';
import {AwButton, AwErrorState, AwFilterBar, AwLoadingState, AwSelect} from '@ascension-tracker/ascension-ui';
import {SectionColumn} from '../../components/SectionColumn.js';
import {useDialogs} from '../../components/DialogProvider.js';
import {ApiClientError} from '../../api.js';
import {
  useCreateTask,
  useMoveTask,
  useProjectReleases,
  useProjectSections,
  useProjectTasks,
  useToggleTaskComplete,
  type Task
} from '../../hooks/useTasks.js';

const priorityOptions = ['critical', 'high', 'medium', 'low'] as const;
const environmentOptions = ['development', 'testing', 'integration', 'early_access', 'production'] as const;

export function BoardView({projectId, onOpenTask}: {projectId: string; onOpenTask: (taskId: string) => void}) {
  const {alert} = useDialogs();
  const sections = useProjectSections(projectId);
  const tasks = useProjectTasks(projectId);
  const createTask = useCreateTask(projectId);
  const moveTask = useMoveTask(projectId);
  const toggleComplete = useToggleTaskComplete(projectId);
  const releases = useProjectReleases(projectId);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [releaseFilter, setReleaseFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');

  const error = (sections.error as Error | null)?.message || (tasks.error as Error | null)?.message;
  if (error) return <AwErrorState>{error}</AwErrorState>;
  if (!sections.data || !tasks.data) return <AwLoadingState/>;

  const boardSections = sections.data.filter(section => !section.isBacklog);
  const defaultSection = boardSections.find(section => section.isDefault) ?? boardSections[0] ?? null;
  const columns = boardSections.map(section => ({id: section.id, name: section.name, isDefault: section.isDefault, workflowState: section.workflowState}));
  const assignees = Array.from(new Set(tasks.data.map(task => task.assigneeName).filter((name): name is string => Boolean(name))));
  const filteredTasks = tasks.data.filter(task =>
    (!releaseFilter || task.releaseId === releaseFilter) &&
    (!priorityFilter || task.priority === priorityFilter) &&
    (!environmentFilter || task.environment === environmentFilter) &&
    (!assigneeFilter || task.assigneeName === assigneeFilter)
  );
  const bySection = new Map<string, Task[]>();
  for (const task of filteredTasks) {
    const key = task.sectionId ?? defaultSection?.id;
    if (!key) continue;
    bySection.set(key, [...(bySection.get(key) ?? []), task]);
  }

  function move(taskId: string, sectionId: string | null, beforeTaskId?: string | null, afterTaskId?: string | null) {
    moveTask.mutate({taskId, sectionId, beforeTaskId, afterTaskId}, {onError: error => { void alert(error instanceof ApiClientError ? error.message : 'Could not move task'); }});
  }

  return (
    <div className="board-view">
      <AwFilterBar>
        <AwSelect id="board-filter-release" label="Release" value={releaseFilter} onChange={event => setReleaseFilter(event.target.value)}>
          <option value="">All</option>
          {releases.data?.map(release => <option key={release.id} value={release.id}>{release.version}</option>)}
        </AwSelect>
        <AwSelect id="board-filter-assignee" label="Assignee" value={assigneeFilter} onChange={event => setAssigneeFilter(event.target.value)}>
          <option value="">All</option>
          {assignees.map(name => <option key={name} value={name}>{name}</option>)}
        </AwSelect>
        <AwSelect id="board-filter-priority" label="Priority" value={priorityFilter} onChange={event => setPriorityFilter(event.target.value)}>
          <option value="">All</option>
          {priorityOptions.map(value => <option key={value} value={value}>{value}</option>)}
        </AwSelect>
        <AwSelect id="board-filter-environment" label="Environment" value={environmentFilter} onChange={event => setEnvironmentFilter(event.target.value)}>
          <option value="">All</option>
          {environmentOptions.map(value => <option key={value} value={value}>{value.replace('_', ' ')}</option>)}
        </AwSelect>
        {(releaseFilter || assigneeFilter || priorityFilter || environmentFilter) && (
          <AwButton type="button" variant="text" onClick={() => { setReleaseFilter(''); setAssigneeFilter(''); setPriorityFilter(''); setEnvironmentFilter(''); }}>Clear filters</AwButton>
        )}
      </AwFilterBar>
      <div className="board-columns">
        {columns.map(column => (
          <SectionColumn
            key={column.id}
            column={column}
            columns={columns}
            tasks={bySection.get(column.id) ?? []}
            onToggle={task => toggleComplete.mutate(task)}
            onOpen={onOpenTask}
            onAdd={title => createTask.mutateAsync({title, sectionId: column.id})}
            onMove={move}
            draggedTaskId={draggedTaskId}
            onDragStart={taskId => setDraggedTaskId(taskId)}
            onDragEnd={() => setDraggedTaskId(null)}
          />
        ))}
      </div>
    </div>
  );
}
